import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  Loader2, Plus, ChefHat, CalendarClock, CalendarX, Pencil, Trash2, Archive,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import AvailabilityDialog from './AvailabilityDialog';
import OverrideDialog from './OverrideDialog';
import ImpactPreviewDialog from './ImpactPreviewDialog';
import StaffPositionsCard from './StaffPositionsCard';
import HoursPatternCard from './HoursPatternCard';
import HoursProposalsCard from './HoursProposalsCard';
import { WEEKDAY_LABELS, addDays, fmtDate, fmtWindow, money, todayStr } from '../../../lib/scheduleFormat';

const KitchenForm = ({ open, kitchen, onClose, onSubmit, busy }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm({
      name: kitchen?.name || '',
      address: kitchen?.address || '',
      hourly_rate: kitchen?.hourly_rate ?? 0,
      min_block_minutes: kitchen?.min_block_minutes ?? 60,
      booking_increment_minutes: kitchen?.booking_increment_minutes ?? 30,
      active_from: kitchen?.active_from || todayStr(),
      active_to: kitchen?.active_to || '',
    });
  }, [open, kitchen]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="kitchen-dialog">
        <DialogHeader>
          <DialogTitle>{kitchen ? 'Edit kitchen' : 'Add the kitchen'}</DialogTitle>
          <DialogDescription>
            Only one kitchen can be active on any date, so the active ranges may not overlap.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="kitchen-name">Name</Label>
            <Input id="kitchen-name" value={form.name || ''}
              onChange={(e) => set({ name: e.target.value })} data-testid="kitchen-name" />
          </div>
          <div>
            <Label htmlFor="kitchen-address">Address (optional)</Label>
            <Input id="kitchen-address" value={form.address || ''}
              onChange={(e) => set({ address: e.target.value })} data-testid="kitchen-address" />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label htmlFor="kitchen-rate">Hourly rate</Label>
              <Input id="kitchen-rate" type="number" min="0" step="0.01" value={form.hourly_rate ?? 0}
                onChange={(e) => set({ hourly_rate: Number(e.target.value) })} data-testid="kitchen-rate" />
            </div>
            <div>
              <Label htmlFor="kitchen-min-block">Minimum block (min)</Label>
              <Input id="kitchen-min-block" type="number" min="5" step="5" value={form.min_block_minutes ?? 60}
                onChange={(e) => set({ min_block_minutes: Number(e.target.value) })}
                data-testid="kitchen-min-block" />
            </div>
            <div>
              <Label htmlFor="kitchen-increment">Increment (min)</Label>
              <Input id="kitchen-increment" type="number" min="5" step="5"
                value={form.booking_increment_minutes ?? 30}
                onChange={(e) => set({ booking_increment_minutes: Number(e.target.value) })}
                data-testid="kitchen-increment" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="kitchen-active-from">First production date</Label>
              <Input id="kitchen-active-from" type="date" value={form.active_from || ''}
                onChange={(e) => set({ active_from: e.target.value })} data-testid="kitchen-active-from" />
            </div>
            <div>
              <Label htmlFor="kitchen-active-to">Last production date (optional)</Label>
              <Input id="kitchen-active-to" type="date" value={form.active_to || ''}
                onChange={(e) => set({ active_to: e.target.value })} data-testid="kitchen-active-to" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kitchen-cancel">Cancel</Button>
          <Button
            onClick={() => onSubmit({ ...form, active_to: form.active_to || null })}
            disabled={busy || !form.name || !form.active_from}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="kitchen-save"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {kitchen ? 'Save kitchen' : 'Add kitchen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KitchenSetup = () => {
  const [kitchens, setKitchens] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [staff, setStaff] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [kitchenForm, setKitchenForm] = useState(null);
  const [availabilityForm, setAvailabilityForm] = useState(null);
  const [overrideForm, setOverrideForm] = useState(null);
  const [impact, setImpact] = useState(null);

  const activeKitchen = kitchens.find((k) => k.id === activeId) || null;

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [kitchenRows, staffRows, positionRows] = await Promise.all([
        adminService.getKitchens(),
        adminService.getSchedulingStaff(),
        adminService.getSchedulingPositions(),
      ]);
      setKitchens(kitchenRows);
      setStaff(staffRows);
      setPositions(positionRows);
      setActiveId((prev) => prev || kitchenRows[0]?.id || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the kitchen setup');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadKitchenDetail = useCallback(async (kitchenId) => {
    if (!kitchenId) { setVersions([]); setOverrides([]); return; }
    try {
      const [availability, overrideRows] = await Promise.all([
        adminService.getKitchenAvailability(kitchenId),
        adminService.getKitchenOverrides(kitchenId, addDays(todayStr(), -30), addDays(todayStr(), 120)),
      ]);
      setVersions(availability.versions);
      setOverrides(overrideRows);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load the access agreement');
    }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadKitchenDetail(activeId); }, [activeId, loadKitchenDetail]);

  const saveKitchen = async (payload) => {
    setBusy(true);
    try {
      if (kitchenForm.kitchen) {
        await adminService.updateKitchen(kitchenForm.kitchen.id, payload);
        toast.success('Kitchen updated');
      } else {
        const created = await adminService.createKitchen(payload);
        setActiveId(created.id);
        toast.success('Kitchen added — now enter the landlord\u2019s access windows');
      }
      setKitchenForm(null);
      await loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the kitchen');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (kitchen) => {
    setBusy(true);
    try {
      await adminService.archiveKitchen(kitchen.id);
      toast.success('Kitchen archived');
      setActiveId(null);
      await loadBase();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not archive the kitchen');
    } finally {
      setBusy(false);
    }
  };

  // Shrinking availability previews what it would invalidate before applying.
  const saveAvailability = async (payload) => {
    const editing = availabilityForm.version;
    setBusy(true);
    try {
      const preview = await adminService.previewAvailabilityImpact({
        kitchen_id: activeId,
        kind: 'version',
        effective_from: payload.effective_from,
        windows: payload.windows,
      }).catch(() => ({ has_impact: false }));

      const apply = async () => {
        if (editing) {
          await adminService.updateAvailabilityVersion(editing.id, payload);
          toast.success('Agreement updated');
        } else {
          await adminService.createAvailabilityVersion(activeId, payload);
          toast.success('New agreement recorded');
        }
      };

      if (preview.has_impact) {
        setImpact({
          impact: preview,
          title: 'These hours no longer cover everything you have planned',
          confirmLabel: 'Record it anyway',
          run: apply,
        });
        setBusy(false);
        return;
      }
      await apply();
      setAvailabilityForm(null);
      await loadKitchenDetail(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the agreement');
    } finally {
      setBusy(false);
    }
  };

  const saveOverride = async (payload) => {
    setBusy(true);
    try {
      const preview = await adminService.previewAvailabilityImpact({
        kitchen_id: activeId,
        kind: 'override',
        date: payload.date,
        override_kind: payload.kind,
        windows: payload.windows,
      }).catch(() => ({ has_impact: false }));

      const apply = async () => {
        await adminService.saveKitchenOverride(activeId, payload);
        toast.success('Override saved');
      };

      if (preview.has_impact) {
        setImpact({
          impact: preview,
          title: 'This override breaks plans on that day',
          confirmLabel: 'Save the override',
          run: apply,
        });
        setBusy(false);
        return;
      }
      await apply();
      setOverrideForm(null);
      await loadKitchenDetail(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the override');
    } finally {
      setBusy(false);
    }
  };

  const confirmImpact = async () => {
    setBusy(true);
    try {
      await impact.run();
      setImpact(null);
      setAvailabilityForm(null);
      setOverrideForm(null);
      await loadKitchenDetail(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not apply the change');
    } finally {
      setBusy(false);
    }
  };

  const removeOverride = async (override) => {
    setBusy(true);
    try {
      await adminService.deleteKitchenOverride(override.id);
      toast.success('Override removed');
      await loadKitchenDetail(activeId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove the override');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="kitchen-setup-loading">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="kitchen-setup">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Kitchen setup</h1>
          <p className="mt-1 text-sm text-gray-600">
            The landlord&apos;s agreement and who you schedule. Booked hours and shifts live in the builder.
          </p>
        </div>
        <Button onClick={() => setKitchenForm({ kitchen: null })} className="bg-amber-600 hover:bg-amber-700"
          data-testid="kitchen-add">
          <Plus className="mr-1 h-4 w-4" /> Add kitchen
        </Button>
      </div>

      {kitchens.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center" data-testid="kitchen-empty">
            <ChefHat className="mx-auto h-10 w-10 text-amber-600" />
            <p className="mt-3 font-medium text-gray-900">No kitchen yet</p>
            <p className="mt-1 text-sm text-gray-600">
              Add the kitchen with its first production date, hourly rate, minimum block and booking
              increment, then enter the landlord&apos;s weekly access windows.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {kitchens.map((kitchen) => (
              <Card
                key={kitchen.id}
                className={kitchen.id === activeId ? 'ring-2 ring-amber-400' : ''}
                data-testid={`kitchen-card-${kitchen.id}`}
              >
                <CardContent className="p-4">
                  <button type="button" onClick={() => setActiveId(kitchen.id)} className="w-full text-left">
                    <p className="font-semibold text-gray-900">{kitchen.name}</p>
                    <p className="text-xs text-gray-500">{kitchen.address || 'No address recorded'}</p>
                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-700">
                      <Badge variant="outline" className="bg-white">{money(kitchen.hourly_rate)}/hour</Badge>
                      <Badge variant="outline" className="bg-white">min {kitchen.min_block_minutes}m</Badge>
                      <Badge variant="outline" className="bg-white">{kitchen.booking_increment_minutes}m steps</Badge>
                    </div>
                    <p className="mt-2 text-xs text-gray-600">
                      Active {fmtDate(kitchen.active_from)} → {kitchen.active_to ? fmtDate(kitchen.active_to) : 'ongoing'}
                    </p>
                  </button>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setKitchenForm({ kitchen })}
                      data-testid={`kitchen-edit-${kitchen.id}`}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="text-gray-500"
                      onClick={() => archive(kitchen)} data-testid={`kitchen-archive-${kitchen.id}`}>
                      <Archive className="mr-1 h-3.5 w-3.5" /> Archive
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {activeKitchen && (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <Card data-testid="availability-card">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CalendarClock className="h-5 w-5 text-amber-600" /> Access agreement
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      Effective-dated, so a new agreement never destroys the old one.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setAvailabilityForm({ version: null })}
                    data-testid="availability-add">
                    <Plus className="mr-1 h-4 w-4" /> New
                  </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {versions.length === 0 && (
                    <p className="text-sm text-amber-700" data-testid="availability-empty">
                      No access windows recorded yet — nothing can be booked until there are.
                    </p>
                  )}
                  {versions.map((version) => (
                    <div key={version.id} className="rounded-lg border border-gray-200 p-3"
                      data-testid={`availability-version-${version.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {fmtDate(version.effective_from)} → {version.effective_to ? fmtDate(version.effective_to) : 'ongoing'}
                        </p>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setAvailabilityForm({ version })}
                            data-testid={`availability-edit-${version.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      {version.note && <p className="text-xs text-gray-500">{version.note}</p>}
                      <div className="mt-2 grid gap-1 text-xs text-gray-700 sm:grid-cols-2">
                        {WEEKDAY_LABELS.map((label, weekday) => {
                          const dayWindows = version.windows.filter((w) => w.weekday === weekday);
                          return (
                            <p key={weekday}>
                              <span className="inline-block w-9 font-medium text-gray-900">{label}</span>
                              {dayWindows.length === 0
                                ? <span className="text-gray-400">closed</span>
                                : dayWindows.map((w) => fmtWindow(w.start_time, w.end_time)).join(', ')}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card data-testid="overrides-card">
                <CardHeader className="flex flex-row items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CalendarX className="h-5 w-5 text-amber-600" /> Day overrides
                    </CardTitle>
                    <p className="text-sm text-gray-500">
                      Closures, replaced hours, or extra hours granted on request.
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setOverrideForm({ override: null })}
                    data-testid="override-add">
                    <Plus className="mr-1 h-4 w-4" /> Add
                  </Button>
                </CardHeader>
                <CardContent className="space-y-2">
                  {overrides.length === 0 && (
                    <p className="text-sm text-gray-500" data-testid="overrides-empty">
                      No overrides in the next four months.
                    </p>
                  )}
                  {overrides.map((override) => (
                    <div key={override.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 p-3"
                      data-testid={`override-row-${override.id}`}>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{fmtDate(override.date)}</p>
                        <p className="text-xs text-gray-600">
                          {override.kind === 'closed'
                            ? 'Closed'
                            : `${override.kind === 'extra' ? 'Extra' : 'Replaced'} — ${override.windows.map((w) => fmtWindow(w.start_time, w.end_time)).join(', ')}`}
                          {override.reason && ` · ${override.reason}`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setOverrideForm({ override })}
                          data-testid={`override-edit-${override.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-600"
                          onClick={() => removeOverride(override)} data-testid={`override-delete-${override.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              </div>
              <HoursPatternCard kitchen={activeKitchen} />
              <HoursProposalsCard kitchen={activeKitchen} />
            </>
          )}
        </>
      )}

      <StaffPositionsCard
        staff={staff}
        positions={positions}
        onSave={async (userId, payload) => {
          await adminService.updateSchedulingStaff(userId, payload);
          toast.success('Positions saved');
          const rows = await adminService.getSchedulingStaff();
          setStaff(rows);
        }}
      />

      {kitchenForm && (
        <KitchenForm open kitchen={kitchenForm.kitchen} busy={busy}
          onClose={() => setKitchenForm(null)} onSubmit={saveKitchen} />
      )}
      {availabilityForm && (
        <AvailabilityDialog open kitchen={activeKitchen} version={availabilityForm.version} busy={busy}
          onClose={() => setAvailabilityForm(null)} onSubmit={saveAvailability} />
      )}
      {overrideForm && (
        <OverrideDialog open kitchen={activeKitchen} override={overrideForm.override} busy={busy}
          onClose={() => setOverrideForm(null)} onSubmit={saveOverride} />
      )}
      {impact && (
        <ImpactPreviewDialog open impact={impact.impact} title={impact.title}
          confirmLabel={impact.confirmLabel} busy={busy}
          onConfirm={confirmImpact} onCancel={() => setImpact(null)} />
      )}
    </div>
  );
};

export default KitchenSetup;
